import { formatNumber } from '@progress/kendo-intl';
import { Button } from '@progress/kendo-react-buttons';
import { Grid, GridColumn as Column, GridHandle, GridItemChangeEvent, GridRowDoubleClickEvent, GridToolbar, GridCustomCellProps } from '@progress/kendo-react-grid';
import { Loader } from '@progress/kendo-react-indicators';
import { NumericTextBox, NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { JsonResponse } from 'TypeGen/json-response';
import CenterDivPanel from '../../components/CenterDivPanel';
import useAlert from '../../components/useAlert';
import useConfirm from '../../components/useConfirm';
import { RightAlignHeaderCell } from '../../components/cells/RightAlignHeaderCell';
import { fetchApi } from '../../services/api';
import { RateFuelUnitOfMeasure } from '../../TypeGen/rate-fuel-unit-of-measure';
import { IDName } from '../../types/idname';
import { ILink } from '../../types/link';
import { VehicleRateBreak } from './VehicleRateBreakStructure';

type Props = {
  RateFuelMasterID: number
};

type RateMasterSummaryResponse = {
  SupportedVehicleRateBreaks: IDName[],
  UnitOfMeasureList: IDName[],
  Links: ILink[]
};

interface EditCommandCellProps extends GridCustomCellProps {
  edit: (item: VehicleRateBreak) => void;
  remove: (item: VehicleRateBreak) => void;
  add: (item: VehicleRateBreak) => void;
  discard: () => void;
  update: (item: VehicleRateBreak) => void;
  cancel: (item: VehicleRateBreak) => void;
  editField: string;
}

const VehicleBreakCell = (props: GridCustomCellProps) => {
  const dataItem: VehicleRateBreak = props.dataItem;
  const dataItemVehicleRateBreak = dataItem.RateBreaks.find(x => x.RateBreakID === Number(props.field));

  const handleChange = (e: NumericTextBoxChangeEvent) => {
    dataItemVehicleRateBreak.Rate = e.target.value;
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: 'RateBreaks',
        syntheticEvent: e.syntheticEvent,
        value: dataItem.RateBreaks
      });
    }
  };

  let rateUoMformat = "n3";
  if (dataItem.UnitOfMeasureID == RateFuelUnitOfMeasure.PercOfLH) {
    rateUoMformat = "p2";
  }

  return (
    <td {...props.tdProps} style={{ textAlign: "right" }}>
      {dataItem.inEdit ? <NumericTextBox format={rateUoMformat} value={dataItemVehicleRateBreak.Rate} onChange={handleChange} min={0} step={0.01} inputStyle={{ textAlign: "right" }} /> :
        <span>{formatNumber(dataItemVehicleRateBreak.Rate, rateUoMformat)}</span>
      }
    </td>
  );
};

const RateCell = (props: GridCustomCellProps) => {
  const dataItem: VehicleRateBreak = props.dataItem;

  const handleChange = (e: NumericTextBoxChangeEvent) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.value
      });
    }
  };

  return (
    <td {...props.tdProps} style={{ textAlign: "right" }}>
      {dataItem.inEdit ? <NumericTextBox format="n3" value={props.dataItem[props.field]} onChange={handleChange} min={0} step={0.25} inputStyle={{ textAlign: "right" }} /> :
        <span>{formatNumber(props.dataItem[props.field], "n3")}</span>
      }
    </td>
  );
};

const FuelVehicleRateBreakStructure = (props: Props) => {
  const gridRefPerMile = useRef<GridHandle>(null);
  const gridRefPercOfLH = useRef<GridHandle>(null);
  const { alert } = useAlert();
  const { ConfirmationDialog, confirm } = useConfirm({});
  const [loading, setLoading] = useState(true);
  //const [isTierComponentInEditMode, setIsTierComponentInEditMode] = useState(false);
  const editField: string = "inEdit";
  const [vehicleRateBreaks, setVehicleRateBreaks] = useState<VehicleRateBreak[]>([]);
  const [CSIDataCopy, setCSIDataCopy] = useState<VehicleRateBreak[]>([]);
  const [rateMasterSummaryResponse, setRateMasterSummaryResponse] = useState<RateMasterSummaryResponse>();


  const UnitOfMeasureCell = (props: GridCustomCellProps) => {
    const dataItem: VehicleRateBreak = props.dataItem;

    return (
      <td {...props.tdProps}>
        <div>{rateMasterSummaryResponse.UnitOfMeasureList.find((x: IDName) => x.ID == dataItem.UnitOfMeasureID).Name}</div>
      </td>
    );

    //const handleChange = (e: DropDownListChangeEvent) => {
    //  if (props.onChange) {

    //    props.onChange({
    //      dataIndex: 0,
    //      dataItem: props.dataItem,
    //      field: props.field,
    //      syntheticEvent: e.syntheticEvent,
    //      value: e.target.value.ID,
    //    });
    //  }
    //};

    //return (
    //  <td {...props.tdProps}>
    //    {dataItem.inEdit ? <DropDownList
    //      className="my-1"
    //      data={rateMasterSummaryResponse.UnitOfMeasureList}
    //      textField="Name"
    //      dataItemKey="ID"
    //      value={rateMasterSummaryResponse.UnitOfMeasureList.find((x: IDName) => x.ID == dataItem.UnitOfMeasureID)}
    //      onChange={handleChange}
    //    /> :
    //      <div>{rateMasterSummaryResponse.UnitOfMeasureList.find((x: IDName) => x.ID == dataItem.UnitOfMeasureID).Name}</div>
    //    }
    //  </td>
    //);
  };

  const EditCommandCell = (props: EditCommandCellProps) => {
    const inEdit = props.dataItem[props.editField];
    const dataItem: VehicleRateBreak = props.dataItem;
    const isNewItem = dataItem.isNew;

    const disabled = ((dataItem.PumpRateMin > 0 && dataItem.PumpRateMax > 0) == false || dataItem.RateBreaks.some(x => x.Rate == null)) || dataItem.UnitOfMeasureID == 0;

    return inEdit ? (
      <td {...props.tdProps} className={[props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
        <Button themeColor={"primary"}
          disabled={disabled}
          onClick={() =>
            isNewItem ? props.add(dataItem) : props.update(dataItem)
          }
        >
          {isNewItem ? "Save" : "Update"}
        </Button>
        <Button themeColor={"secondary"}
          onClick={() =>
            isNewItem ? props.discard() : props.cancel(dataItem)
          }
        >
          {isNewItem ? "Discard" : "Cancel"}
        </Button>
      </td>
    ) : (
      <td {...props.tdProps} className={[props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
        <Button themeColor={"primary"} disabled={vehicleRateBreaks.some(x => x.inEdit === true)}
          onClick={() => props.edit(dataItem)}>Edit</Button>
        <Button themeColor={"secondary"} disabled={vehicleRateBreaks.some(x => x.inEdit === true)}
          onClick={async () => {
            if (!await confirm(`Remove [${dataItem.PumpRateMin}/${dataItem.PumpRateMax}]?`))
              return;
            props.remove(dataItem);
          }}>Delete</Button>
      </td>
    );
  };

  const MyEditCommandCell = (props: GridCustomCellProps) => (
    <EditCommandCell {...props}
      edit={enterEdit}
      remove={remove}
      add={add}
      discard={discard}
      update={update}
      cancel={cancel}
      editField={editField}
    />
  );

  const enterEdit = (dataItem: VehicleRateBreak) => {
    if (vehicleRateBreaks.some(x => x.inEdit === true))
      return;

    setVehicleRateBreaks(
      vehicleRateBreaks.map((item) =>
        item.RateLineItemID === dataItem.RateLineItemID ? { ...item, inEdit: true } : item
      )
    );
  };

  const remove = (dataItem: VehicleRateBreak) => {
    updateAccessorialRateStructure({ ...dataItem, Delete: true });
  };

  const add = (dataItem: VehicleRateBreak) => {
    updateAccessorialRateStructure(dataItem);
  };

  const update = (dataItem: VehicleRateBreak) => {
    updateAccessorialRateStructure(dataItem);
  };

  const cancel = (dataItem: VehicleRateBreak) => {
    const originalItem = CSIDataCopy.find(
      (p) => p.RateLineItemID === dataItem.RateLineItemID
    );
    const newData = vehicleRateBreaks.map((item) =>
      item.RateLineItemID === originalItem.RateLineItemID ? cloneObject(originalItem) : item
    );

    setVehicleRateBreaks(newData);
  };

  const generateId = () =>
    vehicleRateBreaks.reduce((acc, current) => Math.max(acc, current.RateLineItemID), 0) + 1;

  const addNew = (RateUoM: RateFuelUnitOfMeasure) => {
    const newDataItem = {
      inEdit: true, RateLineItemID: generateId(), isNew: true, UnitOfMeasureID: RateUoM,
      RateBreaks: rateMasterSummaryResponse.SupportedVehicleRateBreaks.map(x => {
        return { RateBreakID: x.ID, Rate: 0 };
      })
    } as VehicleRateBreak;
    setVehicleRateBreaks([newDataItem, ...vehicleRateBreaks]);

    if (RateUoM == RateFuelUnitOfMeasure.PerMile && gridRefPerMile.current) {
      gridRefPerMile.current.scrollIntoView({ rowIndex: 0 });
    }

    if (RateUoM == RateFuelUnitOfMeasure.PercOfLH && gridRefPerMile.current) {
      gridRefPercOfLH.current.scrollIntoView({ rowIndex: 0 });
    }
  };

  const discard = () => {
    const newData = [...vehicleRateBreaks];
    newData.splice(0, 1);
    setVehicleRateBreaks(newData);
  };

  const getRateMaster = useCallback(() => {
    setLoading(true);

    fetchApi(`/api/Quote/GetRateFuelMasterSummary/${props.RateFuelMasterID}`)
      .then((response: RateMasterSummaryResponse) => {
        setRateMasterSummaryResponse(response);
      })
      .catch(async e => {
        if (!e?.status)
          await alert('Error: Please see admin');
        else if (e.status !== 404) {
          await alert(e?.detail);
        }
        setLoading(false);
      });
  }, [props.RateFuelMasterID, alert]);


  const cloneObject = (obj: any): any => {
    return JSON.parse(JSON.stringify(obj));
  }

  const refresh = useCallback(() => {
    setLoading(true);

    const link = rateMasterSummaryResponse.Links.find(x => x.Method === 'GET');

    fetchApi(link.Link)
      .then((response: any) => {
        setVehicleRateBreaks(response.VehicleRateBreaks);
        setCSIDataCopy(cloneObject(response.VehicleRateBreaks)) //deep copy/clone

        setLoading(false);
      })
      .catch(async e => {
        if (!e?.status)
          await alert('Error: Please see admin');
        else if (e.status !== 404) {
          await alert(e?.detail);
        }
        setLoading(false);
      });
  }, [rateMasterSummaryResponse?.Links, alert]);

  const updateAccessorialRateStructure = (dataItem: VehicleRateBreak) => {
    setLoading(true);

    const link = rateMasterSummaryResponse.Links.find(x => x.Method === 'POST');
    const data = { ...dataItem }
    fetchApi(link.Link, data, link.Method)
      .then(async (response: JsonResponse) => {
        if (response.Success === false) {
          setLoading(false);
          await alert(`Error: ${response.ErrorMessage}`);
        }
        else
          refresh();
      })
      .catch(async e => {
        setLoading(false);
        // If not problem details
        if (!e?.status) await alert('An error occurred while saving.');
      });
  }

  useEffect(() => {
    getRateMaster();
  }, [props.RateFuelMasterID, getRateMaster]);

  useEffect(() => {
    if (rateMasterSummaryResponse) {
      refresh();
    }
  }, [rateMasterSummaryResponse, refresh]);

  const itemChange = (event: GridItemChangeEvent) => {
    const field = event.field || "";
    const newData = vehicleRateBreaks.map((item) =>
      item.RateLineItemID === event.dataItem.RateLineItemID
        ? { ...item, [field]: event.value }
        : item
    );

    setVehicleRateBreaks(newData);
  }

  const dataView = () => {
    return (<div className="container-fluid mt-2" style={{ position: "relative" }}>
      {loading && <CenterDivPanel>
        <Loader type="converging-spinner" />
      </CenterDivPanel>}

      <h4 className="text-left">Per Mile Fuel Break Point Rate Structure</h4>

      <Grid
        style={{
          maxHeight: `350px`,
        }}
        ref={gridRefPerMile}
        onRowDoubleClick={(e: GridRowDoubleClickEvent) => enterEdit(e.dataItem)}
        data={vehicleRateBreaks.filter(x => x.UnitOfMeasureID == RateFuelUnitOfMeasure.PerMile)}
        onItemChange={itemChange}
      >
        <GridToolbar>
          <Button themeColor={"primary"}
            disabled={vehicleRateBreaks.some(x => x.inEdit === true)}
            title="Add New Per Mile Rate"
            onClick={() => addNew(RateFuelUnitOfMeasure.PerMile)}
          >
            Add New Per Mile Rate
          </Button>
        </GridToolbar>
        <Column field="UnitOfMeasureID" title="Rate UoM" cells={{ data: UnitOfMeasureCell }} />
        <Column field="PumpRateMin" title="Min" cells={{ data: RateCell, headerCell: RightAlignHeaderCell }} />
        <Column field="PumpRateMax" title="Max" cells={{ data: RateCell, headerCell: RightAlignHeaderCell }} />
        {rateMasterSummaryResponse && rateMasterSummaryResponse.SupportedVehicleRateBreaks.map((x, key) => {
          return <Column key={key} title={`${x.Name}`} field={`${x.ID}`} editable={false} cells={{ data: VehicleBreakCell, headerCell: RightAlignHeaderCell }} />
        })}
        <Column cells={{ data: MyEditCommandCell }} width={160} />
      </Grid>

      <h4 className="text-left mt-4">% of LH Fuel Break Point Rate Structure</h4>

      <Grid
        style={{
          maxHeight: `350px`,
        }}
        ref={gridRefPercOfLH}
        onRowDoubleClick={(e: GridRowDoubleClickEvent) => enterEdit(e.dataItem)}
        data={vehicleRateBreaks.filter(x => x.UnitOfMeasureID == RateFuelUnitOfMeasure.PercOfLH)}
        onItemChange={itemChange}
      >
        <GridToolbar>
          <Button themeColor={"primary"}
            disabled={vehicleRateBreaks.some(x => x.inEdit === true)}
            title="Add New % of LH Rate"
            onClick={() => addNew(RateFuelUnitOfMeasure.PercOfLH)}
          >
            Add New % of LH Rate
          </Button>
        </GridToolbar>
        <Column field="UnitOfMeasureID" title="Rate UoM" cells={{ data: UnitOfMeasureCell }} />
        <Column field="PumpRateMin" title="Min" cells={{ data: RateCell, headerCell: RightAlignHeaderCell }} />
        <Column field="PumpRateMax" title="Max" cells={{ data: RateCell, headerCell: RightAlignHeaderCell }} />
        {rateMasterSummaryResponse && rateMasterSummaryResponse.SupportedVehicleRateBreaks.map((x, key) => {
          return <Column key={key} title={`${x.Name}`} field={`${x.ID}`} editable={false} cells={{ data: VehicleBreakCell, headerCell: RightAlignHeaderCell }} />
        })}
        <Column cells={{ data: MyEditCommandCell }} width={160} />
      </Grid>
      <ConfirmationDialog />
    </div>);
  };

  return dataView();
}


export default FuelVehicleRateBreakStructure;
